import React from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShoppingBag, Package, Folder, User, ArrowUpRight, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";

/**
 * Reduced main navigation (Compra · Vendi · Pratiche · Profilo).
 * Home is reachable via the brand tap in the header.
 * "Strumenti" (AI Copilot, KPI dashboard, trend chart, watchlist) sits under
 * Profile → Strumenti and is intentionally out of the main menu.
 */
const items = [
  { to: "/marketplace", key: "buy", Icon: ShoppingBag, testid: "nav-marketplace" },
  { to: "/le-mie-auto", key: "sell", Icon: Package, testid: "nav-sell" },
  { to: "/pratiche", key: "pratiche", Icon: Folder, testid: "nav-pratiche" },
  { to: "/profile", key: "profile", Icon: User, testid: "nav-profile" },
];

export default function BottomNav() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  if (pathname.startsWith("/admin")) return null;

  const navItems = isAdmin
    ? [...items, { to: "/admin", key: "admin", Icon: ShieldCheck, testid: "nav-admin" }]
    : items;

  return (
    <nav
      data-testid="bottom-nav"
      className="fixed bottom-0 inset-x-0 z-50 border-t border-slate-200 bg-white/90 backdrop-blur-xl md:hidden"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <ul className={`grid ${isAdmin ? "grid-cols-5" : "grid-cols-4"}`}>
        {navItems.map(({ to, key, Icon, testid }) => (
          <li key={to}>
            <NavLink
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 py-2.5 text-[11px] font-medium transition-colors ${
                  isActive ? "text-blue-600" : "text-slate-500 hover:text-slate-900"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon className={`w-5 h-5 ${isActive ? "stroke-[2.4]" : "stroke-2"}`} />
                  <span className="tracking-tight">{t(`nav.${key}`)}</span>
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export function SideNav() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  if (pathname.startsWith("/admin")) return null;
  return (
    <aside
      data-testid="side-nav"
      className="hidden md:flex fixed left-0 top-14 bottom-0 w-56 flex-col border-r border-slate-200 bg-white/70 backdrop-blur-xl px-3 py-6"
    >
      <ul className="flex flex-col gap-1">
        {items.map(({ to, key, Icon, testid }) => (
          <li key={to}>
            <NavLink
              to={to}
              data-testid={`side-${testid}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-blue-50 text-blue-700"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              <span>{t(`nav.${key}`)}</span>
            </NavLink>
          </li>
        ))}
        {isAdmin && (
          <li>
            <NavLink
              to="/admin"
              data-testid="side-nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                  isActive
                    ? "bg-slate-900 text-white"
                    : "text-slate-700 hover:bg-slate-100"
                }`
              }
            >
              <ShieldCheck className="w-4 h-4" />
              <span>{t("nav.admin")}</span>
            </NavLink>
          </li>
        )}
      </ul>
      <div className="mt-auto flex flex-col gap-3">
        <Link
          to="/strumenti"
          data-testid="side-nav-strumenti-link"
          className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 hover:text-blue-600 transition-colors"
        >
          {t("nav.strumenti")}
          <ArrowUpRight className="w-3 h-3" />
        </Link>
        <Link
          to="/tariffario"
          data-testid="side-nav-tariff-link"
          className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 hover:text-blue-600 transition-colors"
        >
          {t("nav.tariff")}
          <ArrowUpRight className="w-3 h-3" />
        </Link>
        <div className="text-[10px] tracking-[0.2em] uppercase text-slate-400">AUTEXIO</div>
      </div>
    </aside>
  );
}
